import React, { useState, useEffect } from 'react';
import { 
  FaTicketAlt, 
  FaTags, 
  FaUserPlus,
  FaBuilding,
  FaExchangeAlt,
  FaCheckCircle
} from 'react-icons/fa';
import { db } from '../../firebase';
import { 
  collection, 
  query, 
  where, 
  doc,
  getDocs, 
  getDoc,
  setDoc,
  addDoc, 
  updateDoc, 
  orderBy,
  serverTimestamp, 
  deleteDoc,
  arrayUnion,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion'; 
import styles from '../CSS/TicketSystem.module.css';

// Componentes internos
import TicketsTable from './TicketsTable';
import EtiquetasModal from './EtiquetasModal';
import CreateTicketModal from './CreateTicketModal';
import DepartamentosModal from './DepartamentosModal';
import ResponseTicketModal from './ResponseTicketModal';
import AdminDepartamentoModal from './AdminDepartamentoModal';
import TicketDetailsModal from './TicketDetailsModal';
import CloseTicketModal from './CloseTicketModal';
import FileAttachmentHandler from './FileAttachmentHandler';
import TicketFilters from './TicketFilters'; // Nuevo componente de filtros

const TicketSystem = ({ user }) => {
  // Estados principales
  const [tickets, setTickets] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [departamentos, setDepartamentos] = useState([]);
  const [etiquetas, setEtiquetas] = useState([]);
  const [adminsDepartamento, setAdminsDepartamento] = useState([]);
  const [modalOpen, setModalOpen] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [filterCriteria, setFilterCriteria] = useState({
    estado: 'todos',
    departamento: 'todos',
    asignado: 'todos'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('created_at_desc');
  const [loading, setLoading] = useState(true);
  const [isAdminDepartamento, setIsAdminDepartamento] = useState(false);
  const [adminDepartamentoInfo, setAdminDepartamentoInfo] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Estado para controlar items por página

  // FUNCIÓN PARA ACTUALIZAR ETIQUETAS DEL MODAL DE ETIQUETAS
  const getUserRoleForTicket = (ticket) => {
    // Si el usuario es admin normal, siempre tiene permisos completos
    if (user.role === 'admin') {
      return 'admin';
    }
    
    // Si no es admin de departamento, comportarse como admin normal
    if (!isAdminDepartamento) {
      return 'admin';
    }
    
    // Si es admin de departamento
    if (isAdminDepartamento && adminDepartamentoInfo?.departamento_id) {
      // Si el ticket pertenece a su departamento, tiene permisos de admin_departamento
      if (ticket.departamento_id === adminDepartamentoInfo.departamento_id) {
        return 'admin_departamento';
      } 
      // Si el ticket fue creado por él pero en otro departamento, solo permisos de usuario
      else if (ticket.created_by === user.uid) {
        return 'user';
      }
      // Si no es de su departamento ni lo creó él, no debería verlo (pero por seguridad)
      else {
        return 'user';
      }
    }
    
    // Fallback
    return 'user';
  };
  
  // Función para determinar si puede realizar acciones administrativas en un ticket
  const canPerformAdminActions = (ticket) => {
    const roleForTicket = getUserRoleForTicket(ticket);
    return roleForTicket === 'admin' || roleForTicket === 'admin_departamento';
  };
  // ✅ NUEVA FUNCIÓN: Enviar notificaciones automáticas de nuevo ticket
const enviarNotificacionNuevoTicket = async (ticket, adminsDepartamento) => {
  try {
    console.log('📧 Enviando notificaciones automáticas para nuevo ticket:', ticket.ticket_id);
    
    const response = await fetch('https://truebavirtual.onrender.com/api/new-ticket/notify-new-ticket', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ticket: {
          ticket_id: ticket.ticket_id,
          titulo: ticket.titulo,
          descripcion: ticket.descripcion,
          departamento_nombre: ticket.departamento_nombre,
          created_by_name: ticket.created_by_name,
          created_by_email: ticket.created_by_email,
          id: ticket.id
        },
        adminsDepartamento: adminsDepartamento
      })
    });

    const result = await response.json();
    
    if (result.success) {
      console.log(`✅ Notificaciones enviadas: ${result.emailsSent}/${result.totalAdmins} administradores`);
    } else {
      console.warn('⚠️ Error enviando notificaciones:', result.message);
    }
    
    return result;
    
  } catch (error) {
    console.error('💥 Error de conexión al enviar notificaciones:', error);
    return { success: false, message: 'Error de conexión con el servidor de notificaciones' };
  }
};
  const handleUpdateEtiquetas = (nuevasEtiquetas) => {
    setEtiquetas(nuevasEtiquetas);
  };

  // NUEVA FUNCIÓN PARA ACTUALIZAR ETIQUETAS DE UN TICKET ESPECÍFICO
  const handleUpdateTicketEtiquetas = async (ticketId, nuevasEtiquetas) => {
    try {
      // 1. Obtener el ticket actual para comparar etiquetas
      const ticketActual = tickets.find(t => t.id === ticketId);
      if (!ticketActual) {
        throw new Error('Ticket no encontrado');
      }

      const etiquetasAnteriores = ticketActual.etiquetas || [];
      
      // 2. Calcular diferencias
      const etiquetasAgregadas = nuevasEtiquetas.filter(id => !etiquetasAnteriores.includes(id));
      const etiquetasRemovidas = etiquetasAnteriores.filter(id => !nuevasEtiquetas.includes(id));
      
      // 3. Obtener nombres de etiquetas para el historial
      const getNombreEtiqueta = (id) => {
        const etiqueta = etiquetas.find(e => e.id === id);
        return etiqueta ? etiqueta.nombre : 'Etiqueta desconocida';
      };

      // 4. Crear entrada de historial si hubo cambios
      let entradaHistorial = null;
      if (etiquetasAgregadas.length > 0 || etiquetasRemovidas.length > 0) {
        entradaHistorial = {
          accion: 'Etiquetas actualizadas',
          usuario_id: user.uid,
          usuario_nombre: user.displayName || user.email,
          fecha: new Date(),
          etiquetas_agregadas: etiquetasAgregadas.map(getNombreEtiqueta),
          etiquetas_removidas: etiquetasRemovidas.map(getNombreEtiqueta)
        };
      }

      // 5. Actualizar el ticket en Firebase
      const ticketRef = doc(db, 'tickets', ticketId);
      const updateData = {
        etiquetas: nuevasEtiquetas,
        updated_at: new Date()
      };

      if (entradaHistorial) {
        updateData.historial = arrayUnion(entradaHistorial);
      }

      await updateDoc(ticketRef, updateData);

      // 6. Actualizar el estado local
      setTickets(prevTickets => 
        prevTickets.map(ticket => 
          ticket.id === ticketId 
            ? {
                ...ticket,
                etiquetas: nuevasEtiquetas,
                updated_at: new Date(),
                historial: entradaHistorial 
                  ? [...(ticket.historial || []), entradaHistorial]
                  : ticket.historial
              }
            : ticket
        )
      );

      // 7. Actualizar el ticket seleccionado si es el mismo
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket(prev => ({
          ...prev,
          etiquetas: nuevasEtiquetas,
          updated_at: new Date(),
          historial: entradaHistorial 
            ? [...(prev.historial || []), entradaHistorial]
            : prev.historial
        }));
      }

      // 8. Mostrar mensaje de éxito
      if (etiquetasAgregadas.length > 0 || etiquetasRemovidas.length > 0) {
        setSuccessMessage('Etiquetas del ticket actualizadas exitosamente');
      }

      console.log('Etiquetas del ticket actualizadas exitosamente');
      return Promise.resolve();

    } catch (error) {
      console.error('Error al actualizar etiquetas del ticket:', error);
      setError('Error al actualizar las etiquetas del ticket. Inténtalo de nuevo.');
      return Promise.reject(error);
    }
  };

  // FUNCIÓN ACTUALIZADA: Reasignar ticket con email del admin
  const handleReasignarTicket = async (ticketId, nuevoAdminId, adminData = {}) => {
    try {
      // 1. Obtener información del ticket actual
      const ticketActual = tickets.find(t => t.id === ticketId);
      if (!ticketActual) {
        throw new Error('Ticket no encontrado');
      }
  
      // 2. Si no se pasan datos del admin, buscarlos
      if (!adminData.assigned_to_email || !adminData.assigned_to_name) {
        const nuevoAdmin = adminsDepartamento.find(a => a.id === nuevoAdminId);
        
        adminData = {
          assigned_to_email: nuevoAdmin?.email || '',
          assigned_to_name: nuevoAdmin?.nombre || nuevoAdmin?.displayName || 'Admin'
        };
      }
      
      // 3. Obtener información del admin anterior
      const adminAnterior = adminsDepartamento.find(a => a.id === ticketActual.assigned_to);
      
      console.log('🎯 Reasignando ticket:', {
        de: adminAnterior,
        a: adminData
      });
  
      // 4. Actualizar el ticket en la base de datos
      const ticketRef = doc(db, 'tickets', ticketId);
      
      // Crear entrada de historial para la reasignación
      const entradaHistorial = {
        accion: 'Ticket reasignado',
        usuario_id: user.uid,
        usuario_nombre: user.displayName || user.email,
        fecha: new Date(),
        reasignado_de: ticketActual.assigned_to,
        reasignado_de_nombre: adminAnterior?.nombre || 'Admin anterior',
        reasignado_de_email: adminAnterior?.email || '', // ✅ NUEVO
        reasignado_a: nuevoAdminId,
        reasignado_a_nombre: adminData.assigned_to_name,
        reasignado_a_email: adminData.assigned_to_email // ✅ NUEVO
      };
  
      // Actualizar el documento
      await updateDoc(ticketRef, {
        assigned_to: nuevoAdminId,
        assigned_to_name: adminData.assigned_to_name,
        assigned_to_email: adminData.assigned_to_email, // ✅ NUEVO
        updated_at: new Date(),
        historial: arrayUnion(entradaHistorial)
      });
  
      // 5. Actualizar el estado local
      setTickets(prevTickets => 
        prevTickets.map(ticket => 
          ticket.id === ticketId 
            ? {
                ...ticket,
                assigned_to: nuevoAdminId,
                assigned_to_name: adminData.assigned_to_name,
                assigned_to_email: adminData.assigned_to_email, // ✅ NUEVO
                updated_at: new Date(),
                historial: [...(ticket.historial || []), entradaHistorial]
              }
            : ticket
        )
      );
  
      // 6. Mostrar mensaje de éxito
      setSuccessMessage(`Ticket reasignado exitosamente a ${adminData.assigned_to_name}`);
      
      console.log('Ticket reasignado exitosamente');
      return Promise.resolve();
  
    } catch (error) {
      console.error('Error al reasignar ticket:', error);
      setError('Error al reasignar el ticket. Inténtalo de nuevo.');
      return Promise.reject(error);
    }
  };
  
  
  
  // Cargar datos iniciales
  // Reemplazar la sección del useEffect de carga de datos (líneas aproximadamente 180-350)
// con esta versión corregida:

useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Verificar si el usuario es admin de departamento
      const adminQuery = query(
        collection(db, 'admins_departamento'),
        where('email', '==', user.email)
      );
      const adminSnapshot = await getDocs(adminQuery);
      const isAdminDep = !adminSnapshot.empty;
      
      let adminInfo = null;
      if (isAdminDep) {
        adminInfo = adminSnapshot.docs[0].data();
        adminInfo.id = adminSnapshot.docs[0].id;
      }
      
      setIsAdminDepartamento(isAdminDep);
      setAdminDepartamentoInfo(adminInfo);
      
      // 1) Departamentos
      const departamentosQuery = query(collection(db, 'departamentos'));
      const departamentosSnapshot = await getDocs(departamentosQuery);
      const fetchedDepartamentos = departamentosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDepartamentos(fetchedDepartamentos);

      // 2) Admins de departamento
      const adminsQuery = query(collection(db, 'admins_departamento'));
      const adminsSnapshot = await getDocs(adminsQuery);
      setAdminsDepartamento(
        adminsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      );

      // 3) Etiquetas
      const etiquetasQuery = query(collection(db, 'etiquetas'));
      const etiquetasSnapshot = await getDocs(etiquetasQuery);
      setEtiquetas(
        etiquetasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      );

      // 4) Tickets - Lógica simplificada y más robusta
      console.log('🔍 Configurando listeners de tickets...', {
        isAdminDep,
        userRole: user.role,
        adminInfo
      });

      // NUEVA LÓGICA: Usar una sola consulta más amplia para admin de departamento
      if (isAdminDep && adminInfo?.departamento_id && user.role !== 'admin') {
        console.log('📋 Cargando tickets para admin de departamento:', adminInfo.departamento_id);
        
        // Consulta 1: Todos los tickets del departamento del admin
        const ticketsDeptQuery = query(
          collection(db, 'tickets'),
          where('departamento_id', '==', adminInfo.departamento_id),
          orderBy('created_at', 'desc')
        );
        
        // Consulta 2: Todos los tickets creados por este usuario
        const ticketsCreatedQuery = query(
          collection(db, 'tickets'),
          where('created_by', '==', user.uid),
          orderBy('created_at', 'desc')
        );

        // Estado para manejar ambas consultas
        let ticketsDepartamento = [];
        let ticketsCreados = [];
        let consultasCompletadas = 0;

        const combinarTickets = () => {
          console.log('🔄 Combinando tickets:', {
            departamento: ticketsDepartamento.length,
            creados: ticketsCreados.length,
            consultasCompletadas
          });

          // Usar Map para eliminar duplicados por ID
          const ticketsMap = new Map();
          
          // Agregar tickets del departamento
          ticketsDepartamento.forEach(ticket => {
            ticketsMap.set(ticket.id, ticket);
          });
          
          // Agregar tickets creados por el usuario (sin duplicar)
          ticketsCreados.forEach(ticket => {
            if (!ticketsMap.has(ticket.id)) {
              ticketsMap.set(ticket.id, ticket);
            }
          });
          
          // Convertir a array y ordenar
          const ticketsCombinados = Array.from(ticketsMap.values())
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          
          console.log('✅ Tickets combinados:', ticketsCombinados.length);
          
          setTickets(ticketsCombinados);
          setFilteredTickets(ticketsCombinados);
          
          // Solo quitar loading cuando ambas consultas estén listas
          if (consultasCompletadas >= 2) {
            setLoading(false);
          }
        };

        // Listener 1: Tickets del departamento
        const unsubscribeDept = onSnapshot(
          ticketsDeptQuery,
          (snapshot) => {
            ticketsDepartamento = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              created_at: doc.data().created_at?.toDate() || new Date(),
              updated_at: doc.data().updated_at?.toDate() || new Date(),
              closed_at: doc.data().closed_at?.toDate() || null
            }));
            
            consultasCompletadas = Math.max(consultasCompletadas, 1);
            combinarTickets();
          },
          (error) => {
            console.error("❌ Error en tickets del departamento:", error);
            consultasCompletadas = Math.max(consultasCompletadas, 1);
            if (consultasCompletadas >= 2) {
              setLoading(false);
            }
          }
        );

        // Listener 2: Tickets creados por el usuario
        const unsubscribeCreated = onSnapshot(
          ticketsCreatedQuery,
          (snapshot) => {
            ticketsCreados = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              created_at: doc.data().created_at?.toDate() || new Date(),
              updated_at: doc.data().updated_at?.toDate() || new Date(),
              closed_at: doc.data().closed_at?.toDate() || null
            }));
            
            consultasCompletadas = Math.max(consultasCompletadas, 2);
            combinarTickets();
          },
          (error) => {
            console.error("❌ Error en tickets creados por usuario:", error);
            consultasCompletadas = Math.max(consultasCompletadas, 2);
            if (consultasCompletadas >= 1) {
              setLoading(false);
            }
          }
        );

        // Timeout de seguridad para quitar loading
        const timeoutId = setTimeout(() => {
          console.warn('⏰ Timeout: Quitando loading por seguridad');
          setLoading(false);
        }, 10000); // 10 segundos

        // Función de limpieza
        return () => {
          clearTimeout(timeoutId);
          unsubscribeDept();
          unsubscribeCreated();
        };

      } else {
        // Para admin normal: todos los tickets
        console.log('📋 Cargando todos los tickets para admin normal');
        
        const ticketsQuery = query(
          collection(db, 'tickets'),
          orderBy('created_at', 'desc')
        );
        
        const unsubscribeTickets = onSnapshot(
          ticketsQuery,
          (snapshot) => {
            const fetchedTickets = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              created_at: doc.data().created_at?.toDate() || new Date(),
              updated_at: doc.data().updated_at?.toDate() || new Date(),
              closed_at: doc.data().closed_at?.toDate() || null
            }));
            
            console.log('✅ Todos los tickets cargados:', fetchedTickets.length);
            
            setTickets(fetchedTickets);
            setFilteredTickets(fetchedTickets);
            setLoading(false);
          },
          (error) => {
            console.error("❌ Error cargando todos los tickets:", error);
            setLoading(false);
          }
        );
        
        return () => {
          unsubscribeTickets();
        };
      }
      
    } catch (error) {
      console.error('💥 Error al cargar datos iniciales:', error);
      setLoading(false);
    }
  };

  const unsubscribe = fetchData();
  
  return () => {
    if (unsubscribe && typeof unsubscribe === 'function') {
      unsubscribe();
    }
  };
}, [user.email, user.uid, user.role]); // ✅ Dependencias más específicas

  // Efecto para aplicar filtros
  useEffect(() => {
    if (!tickets.length) return;
    
    let result = [...tickets];
    
    // NUEVO: Filtrar tickets cerrados si showClosed es false
    if (!showClosed) {
      result = result.filter(ticket => ticket.estado !== 'cerrado');
    }
    
    // Aplicar filtros existentes
    if (filterCriteria.estado !== 'todos') {
      result = result.filter(ticket => ticket.estado === filterCriteria.estado);
    }
    
    if (filterCriteria.departamento !== 'todos') {
      result = result.filter(ticket => ticket.departamento_id === filterCriteria.departamento);
    }
    
    if (filterCriteria.asignado !== 'todos') {
      if (filterCriteria.asignado === 'sin_asignar') {
        result = result.filter(ticket => !ticket.assigned_to);
      } else {
        result = result.filter(ticket => ticket.assigned_to === filterCriteria.asignado);
      }
    }
    
    // Aplicar búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(ticket => 
        ticket.titulo.toLowerCase().includes(query) || 
        ticket.descripcion.toLowerCase().includes(query) ||
        (ticket.ticket_id && ticket.ticket_id.toLowerCase().includes(query))
      );
    }
    
    // Aplicar ordenamiento SIMPLIFICADO
    if (sortOption === 'created_at_desc') {
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortOption === 'created_at_asc') {
      result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    
    setFilteredTickets(result);
  }, [tickets, filterCriteria, searchQuery, sortOption, showClosed]);
  /* ----- Handlers ----- */
  const handleUpdateAdminDepartamento = async (adminId, updatedData) => {
    try {
      const adminRef = doc(db, 'admins_departamento', adminId);
      
      await updateDoc(adminRef, {
        ...updatedData,
        updated_at: Timestamp.now(),
        updated_by: user.uid
      });
  
      // Actualizar el estado local también
      setAdminsDepartamento(prev => 
        prev.map(admin => 
          admin.id === adminId 
            ? { ...admin, ...updatedData }
            : admin
        )
      );
  
      return true;
    } catch (error) {
      console.error('Error al actualizar admin de departamento:', error);
      return false;
    }
  };

  const handleDeleteAdminDepartamento = async (adminId) => {
    try {
      // 1. Buscar todos los tickets asignados a este admin
      const ticketsAsignadosQuery = query(
        collection(db, 'tickets'),
        where('assigned_to', '==', adminId)
      );
      const ticketsAsignadosSnapshot = await getDocs(ticketsAsignadosQuery);
      
      // Obtener información del admin que se va a eliminar para el historial
      const adminInfo = adminsDepartamento.find(admin => admin.id === adminId);
      const adminNombre = adminInfo?.nombre || 'Admin eliminado';
      
      // 2. Actualizar todos los tickets asignados a este admin
      const updatePromises = ticketsAsignadosSnapshot.docs.map(async (ticketDoc) => {
        const ticketRef = doc(db, 'tickets', ticketDoc.id);
        
        // Crear entrada de historial
        const entradaHistorial = {
          accion: 'Admin eliminado - Ticket desasignado',
          usuario_id: user.uid,
          usuario_nombre: user.displayName || user.email,
          fecha: new Date(),
          admin_eliminado: adminId,
          admin_eliminado_nombre: adminNombre,
          motivo: 'El administrador asignado fue eliminado del sistema'
        };
        
        // Actualizar el ticket
        await updateDoc(ticketRef, {
          assigned_to: null,
          assigned_to_name: null,
          estado: 'nuevo', // Volver a estado nuevo
          updated_at: new Date(),
          historial: arrayUnion(entradaHistorial)
        });
      });
      
      // Esperar a que se actualicen todos los tickets
      await Promise.all(updatePromises);
      
      // 3. Eliminar el admin de departamento
      await deleteDoc(doc(db, 'admins_departamento', adminId));
      
      // 4. Actualizar el estado local de adminsDepartamento
      setAdminsDepartamento(prev => prev.filter(admin => admin.id !== adminId));
      
      // 5. Actualizar el estado local de tickets
      setTickets(prevTickets => 
        prevTickets.map(ticket => 
          ticket.assigned_to === adminId 
            ? {
                ...ticket,
                assigned_to: null,
                assigned_to_name: null,
                estado: 'nuevo',
                updated_at: new Date(),
                historial: [...(ticket.historial || []), {
                  accion: 'Admin eliminado - Ticket desasignado',
                  usuario_id: user.uid,
                  usuario_nombre: user.displayName || user.email,
                  fecha: new Date(),
                  admin_eliminado: adminId,
                  admin_eliminado_nombre: adminNombre,
                  motivo: 'El administrador asignado fue eliminado del sistema'
                }]
              }
            : ticket
        )
      );
      
      // 6. Mostrar mensaje de éxito
      const ticketsAfectados = ticketsAsignadosSnapshot.docs.length;
      if (ticketsAfectados > 0) {
        setSuccessMessage(`Admin eliminado exitosamente. ${ticketsAfectados} ticket(s) fueron desasignados.`);
      } else {
        setSuccessMessage('Admin eliminado exitosamente.');
      }
      
      return true;
    } catch (error) {
      console.error('Error al eliminar admin de departamento:', error);
      setError('Error al eliminar el administrador. Inténtalo de nuevo.');
      return false;
    }
  };
  
  const handleCreateTicket = async (nuevoTicket, files = []) => {
    try {
      // Obtener el siguiente número de ticket
      const counterRef = doc(db, 'counters', 'tickets');
      const counterDoc = await getDoc(counterRef);
      
      let nextValue = 1;
      
      if (counterDoc.exists()) {
        nextValue = counterDoc.data().value + 1;
        await updateDoc(counterRef, { value: nextValue });
      } else {
        await setDoc(counterRef, { value: nextValue });
      }
      
      // Generar el ID formateado
      const ticketId = `#${String(nextValue).padStart(5, '0')}`;
      
      // Subir archivos adjuntos si hay
      let adjuntos = [];
      if (files && files.length > 0) {
        adjuntos = await FileAttachmentHandler.uploadFiles(files);
      }
      
      const ticketData = {
        ...nuevoTicket,
        ticket_id: ticketId,
        created_at: Timestamp.now(),
        updated_at: Timestamp.now(),
        created_by: user.uid,
        created_by_name: user.displayName || user.email,
        estado: 'nuevo',
        assigned_to: null,
        etiquetas: [],
        adjuntos: adjuntos.map(file => ({
          id: file.id,
          nombre: file.name,
          tipo: file.type,
          tamaño: file.size,
          url: file.url,
          localUrl: file.localUrl
        })),
        historial: [
          {
            fecha: Timestamp.now(),
            accion: 'Creación de ticket',
            usuario: user.uid,
            usuario_nombre: user.displayName || user.email,
            tiene_adjuntos: adjuntos.length > 0
          }
        ]
      };
      
      // ✅ PASO 1: Crear el ticket en Firebase
      const docRef = await addDoc(collection(db, 'tickets'), ticketData);
      
      // ✅ PASO 2: Preparar datos completos para notificaciones
      const ticketCompleto = {
        ...ticketData,
        id: docRef.id
      };
      
      // ✅ PASO 3: Enviar notificaciones automáticas
      const adminsDelDepartamento = adminsDepartamento.filter(
        admin => admin.departamento_id === nuevoTicket.departamento_id
      );
      
      if (adminsDelDepartamento.length > 0) {
        await enviarNotificacionNuevoTicket(ticketCompleto, adminsDelDepartamento);
      } else {
        console.warn('⚠️ No hay administradores asignados a este departamento para notificar');
      }
      
      setModalOpen(null);
      return { success: true, ticketId: docRef.id, ticket_id: ticketId };
    } catch (error) {
      console.error('Error al crear ticket:', error);
      return { success: false };
    }
  };

  // FUNCIÓN ACTUALIZADA: Asignar ticket con email del admin
  const handleAsignarTicket = async (ticketId, adminId = user.uid, adminData = {}) => {
    try {
      // Si no se especifica adminId, se usa el ID del usuario actual
      const adminToAssign = adminId || user.uid;
      
      // Si no se pasan datos del admin, buscarlos
      if (!adminData.assigned_to_email || !adminData.assigned_to_name) {
        const adminSeleccionado = adminsDepartamento.find(admin => admin.id === adminToAssign) || {
          id: user.uid,
          email: user.email,
          nombre: user.displayName || user.email
        };
        
        adminData = {
          assigned_to_email: adminSeleccionado.email || '', // ✅ NUEVO
          assigned_to_name: adminSeleccionado.nombre || adminSeleccionado.displayName || 'Admin'
        };
      }
      
      console.log('🎯 Asignando ticket con datos completos del admin:', adminData);
      
      const ticketRef = doc(db, 'tickets', ticketId);
      
      await updateDoc(ticketRef, {
        assigned_to: adminToAssign,
        assigned_to_name: adminData.assigned_to_name,
        assigned_to_email: adminData.assigned_to_email, // ✅ NUEVO: Guardar email
        estado: 'en_proceso',
        updated_at: Timestamp.now(),
        historial: arrayUnion({
          fecha: Timestamp.now(),
          accion: 'Ticket asignado',
          usuario: user.uid,
          usuario_nombre: user.displayName || user.email,
          asignado_a: adminToAssign,
          asignado_a_nombre: adminData.assigned_to_name,
          asignado_a_email: adminData.assigned_to_email // ✅ NUEVO: En historial también
        })
      });
  
      return true;
    } catch (error) {
      console.error('Error al asignar ticket:', error);
      return false;
    }
  };
  
  const handleCerrarTicket = async (ticketId, resolucion) => {
    try {
      const ticketRef = doc(db, 'tickets', ticketId);
  
      await updateDoc(ticketRef, {
        estado: 'cerrado',
        closed_at: Timestamp.now(),
        updated_at: Timestamp.now(),
        resolucion,
        historial: arrayUnion({
          fecha: Timestamp.now(),
          accion: 'Ticket cerrado',
          usuario: user.uid,
          usuario_nombre: user.displayName || user.email,
          resolucion
        })
      });
  
      setSelectedTicket(null);
      setModalOpen(null);
      return true;
    } catch (error) {
      console.error('Error al cerrar ticket:', error);
      return false;
    }
  };

  const handleResponderTicket = async (ticket, respuesta) => {
    const ticketId = ticket.id;
    try {
      // Actualizar el ticket con la respuesta
      await updateDoc(doc(db, 'tickets', ticketId), {
        estado: 'en_proceso',
        updated_at: Timestamp.now(),
        respuestas: arrayUnion({
          texto: respuesta,
          fecha: Timestamp.now(),
          usuario: user.uid,
          usuario_nombre: user.displayName || user.email
        }),
        historial: arrayUnion({
          fecha: Timestamp.now(),
          accion: 'Respuesta agregada',
          usuario: user.uid,
          usuario_nombre: user.displayName || user.email
        })
      });
  
      return true;
    } catch (error) {
      console.error('Error al responder ticket:', error);
      throw error; // Lanzar el error para que el componente lo maneje
    }
  };

  const handleEscalarTicket = async (ticket, departamentoDestinoId, mensaje = null) => {
    try {
      const ticketRef = doc(db, 'tickets', ticket.id);
      const departamentoDestino = departamentos.find(d => d.id === departamentoDestinoId);
      
      // Datos de actualización base
      const updateData = {
        departamento_id: departamentoDestinoId,
        estado: 'escalado',
        // ✅ AGREGAR ESTAS 3 LÍNEAS:
        assigned_to: null,
        assigned_to_name: null,
        assigned_to_email: null,
        // ✅ FIN DEL CAMBIO
        updated_at: serverTimestamp()
      };
      
      // Solo agregar respuesta si hay mensaje
      if (mensaje && mensaje.trim()) {
        const nuevaRespuesta = {
          texto: mensaje,
          fecha: serverTimestamp(),
          usuario: user.uid,
          usuario_nombre: user.displayName || user.email || 'Usuario',
          tipo: 'escalacion'
        };
        
        updateData.respuestas = arrayUnion(nuevaRespuesta);
      }
      
      // Actualizar ticket
      await updateDoc(ticketRef, updateData);
      
      // Crear notificación
      await addDoc(collection(db, 'notificaciones'), {
        tipo: 'ticket_escalado',
        ticket_id: ticket.id,
        ticket_titulo: ticket.titulo,
        departamento_origen: ticket.departamento_id,
        departamento_destino: departamentoDestinoId,
        departamento_destino_nombre: departamentoDestino?.nombre || 'Departamento',
        escalado_por: user.uid,
        escalado_por_nombre: user.displayName || user.email || 'Usuario',
        mensaje: mensaje || null, // null si no hay mensaje
        created_at: serverTimestamp(),
        leida: false
      });
      
      console.log('Ticket escalado exitosamente');
      
    } catch (error) {
      console.error('Error al escalar ticket:', error);
      throw error;
    }
  };

  const handleCreateAdminDepartamento = async (nuevoAdmin) => {
    try {
      const docRef = await addDoc(collection(db, 'admins_departamento'), {
        ...nuevoAdmin,
        created_at: Timestamp.now(),
        created_by: user.uid
      });
  
      // Actualizar el estado local con el nuevo admin
      const newAdmin = {
        id: docRef.id,
        ...nuevoAdmin,
        created_at: Timestamp.now(),
        created_by: user.uid
      };
      
      setAdminsDepartamento(prev => [...prev, newAdmin]);
  
      // No cerrar el modal aquí, dejar que el componente lo maneje
      return true;
    } catch (error) {
      console.error('Error al crear admin de departamento:', error);
      return false;
    }
  };

  // Formatear fecha
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Renderizado del componente
  // Renderizado del componente
return (
  <div className={styles.ticketSystemContainer}>
  {/* Header minimalista que integra las estadísticas */}
  

    
      {/* Sección de acciones principales - Sistema mejorado con grid responsive */}
      <div className={styles.adminActionBar}>
        {/* Acciones primarias - Siempre visibles */}
        <div className={styles.actionGroup}>
          <button
            className={`${styles.adminActionButton} ${styles.primaryAction}`}
            onClick={() => setModalOpen('crear_ticket')}
            title="Crear nuevo ticket"
          >
            <FaTicketAlt />
            <span>Crear Ticket</span>
          </button>

          <button
            className={styles.adminActionButton}
            onClick={() => setModalOpen('etiquetas')}
            title="Gestionar etiquetas"
          >
            <FaTags />
            <span>Etiquetas</span>
          </button>
        </div>

        {/* Acciones administrativas - Solo para admins normales */}
        {(!isAdminDepartamento || user.role === 'admin') && (
          <div className={styles.actionGroup}>
            <button
              className={styles.adminActionButton}
              onClick={() => setModalOpen('departamentos')}
              title="Gestionar departamentos"
            >
              <FaBuilding />
              <span>Departamentos</span>
            </button>

            <button
              className={styles.adminActionButton}
              onClick={() => setModalOpen('admin_departamento')}
              title="Gestionar administradores de departamento"
            >
              <FaUserPlus />
              <span>Gestionar Admins</span>
            </button>
          </div>
        )}
      </div>

      {/* Componente de Filtros */}
      <TicketFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterCriteria={filterCriteria}
        setFilterCriteria={setFilterCriteria}
        sortOption={sortOption}
        setSortOption={setSortOption}
        departamentos={departamentos}
        adminsDepartamento={adminsDepartamento}
        userRole={user.role === 'admin' ? "admin" : (isAdminDepartamento ? "admin_departamento" : "admin")}
        showClosed={showClosed}
        setShowClosed={setShowClosed}
      />

      {/* Tabla de tickets (Componente separado) */}
        <TicketsTable
          loading={loading}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          showPagination={true}
          filteredTickets={filteredTickets}
          departamentos={departamentos}
          adminsDepartamento={adminsDepartamento}
          formatDate={formatDate}
          onViewTicket={(ticket) => {
            setSelectedTicket(ticket);
            setModalOpen('ver_ticket');
          }}
          onRespondTicket={(ticket) => {
            setSelectedTicket(ticket);
            setModalOpen('responder_ticket');
          }}
          onCloseTicket={(ticket) => {
            setSelectedTicket(ticket);
            setModalOpen('cerrar_ticket');
          }}
        />

      {/* --- MODALES --- */}
      <AnimatePresence>
        {modalOpen === 'crear_ticket' && (
          <CreateTicketModal 
            user={user}
            departamentos={departamentos}
            adminsDepartamento={adminsDepartamento}
            onClose={() => setModalOpen(null)}
            onCreate={handleCreateTicket}
          />
        )}

        {/* Solo mostrar modal de departamentos si NO es admin de departamento O si es admin normal */}
        {modalOpen === 'departamentos' && (!isAdminDepartamento || user.role === 'admin') && (
          <DepartamentosModal 
            user={user}
            departamentos={departamentos}
            usuarios={[...adminsDepartamento, { id: user.uid, displayName: user.displayName || user.email, email: user.email }]}
            adminsDepartamento={adminsDepartamento}
            onClose={() => setModalOpen(null)}
            onUpdateDepartamentos={setDepartamentos}
            
          />
        )}

        {/* Solo mostrar modal de admin departamento si NO es admin de departamento O si es admin normal */}
        {modalOpen === 'admin_departamento' && (!isAdminDepartamento || user.role === 'admin') && (
          <AdminDepartamentoModal 
            user={user}
            departamentos={departamentos}
            adminsDepartamento={adminsDepartamento}
            onClose={() => setModalOpen(null)}
            onCreate={handleCreateAdminDepartamento}
            onUpdate={handleUpdateAdminDepartamento}
            onDelete={handleDeleteAdminDepartamento}
          />
        )}

        {modalOpen === 'cerrar_ticket' && selectedTicket && (
          <CloseTicketModal 
            ticket={selectedTicket}
            user={user}
            userRole={getUserRoleForTicket(selectedTicket)} // 🔑 Dinámico según el ticket
            departamentos={departamentos}
            onClose={() => {
              setModalOpen(null);
              setSelectedTicket(null);
            }}
            onCerrar={handleCerrarTicket}
          />
        )}

        {modalOpen === 'responder_ticket' && selectedTicket && (
          <ResponseTicketModal 
            ticket={selectedTicket}
            user={user}
            userRole={getUserRoleForTicket(selectedTicket)} // 🔑 Dinámico según el ticket
            departamentos={departamentos}
            etiquetas={etiquetas}
            adminsDepartamento={adminsDepartamento}
            onClose={() => {
              setModalOpen(null);
              setSelectedTicket(null);
            }}
            onResponder={handleResponderTicket}
            onEscalar={
              canPerformAdminActions(selectedTicket) 
                ? handleEscalarTicket 
                : undefined // Solo si tiene permisos de admin
            }
          />
        )}

        {modalOpen === 'etiquetas' && (
          <EtiquetasModal 
            user={user}
            userRole={user.role === 'admin' ? "admin" : (isAdminDepartamento ? "admin_departamento" : "admin")}
            departamentos={departamentos}
            etiquetas={etiquetas}
            adminsDepartamento={adminsDepartamento}
            onClose={() => setModalOpen(null)}
            onUpdateEtiquetas={handleUpdateEtiquetas}
          />
        )}

          {modalOpen === 'ver_ticket' && selectedTicket && (
            <TicketDetailsModal
              ticket={selectedTicket}
              mode="ver"
              user={user}
              userRole={getUserRoleForTicket(selectedTicket)} // 🔑 Dinámico según el ticket
              departamentos={departamentos}
              etiquetas={etiquetas}
              onUpdateEtiquetas={
                canPerformAdminActions(selectedTicket) 
                  ? handleUpdateTicketEtiquetas 
                  : undefined // Solo si tiene permisos de admin
              }
              adminsDepartamento={adminsDepartamento}
              onClose={() => {
                setModalOpen(null);
                setSelectedTicket(null);
              }}
              onReasignar={
                canPerformAdminActions(selectedTicket) 
                  ? handleReasignarTicket 
                  : undefined // Solo si tiene permisos de admin
              }
              onResponder={() => {
                setModalOpen('responder_ticket');
              }}
              onAsignar={
                canPerformAdminActions(selectedTicket) 
                  ? handleAsignarTicket 
                  : undefined // Solo si tiene permisos de admin
              }
              onCerrar={() => {
                setModalOpen('cerrar_ticket');
              }}
            />
          )}
      </AnimatePresence>
    </div>
  );
};

export default TicketSystem;